import { QuoteForm } from "../components/QuoteForm";
import { SEO } from "../components/SEO";
import { Card, CardContent } from "../components/ui/card";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export const Contact = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Contact Us | Request Free Pergola Quote | Hudson Knox Outdoor Living"
        description="Get your free pergola quote today. Contact Hudson Knox Outdoor Living for luxury pergola installation in South East England. Call 07846 923387 or email admin@hudsonknox.co.uk"
        keywords="contact, free quote, pergola quote, consultation, Hudson Knox contact, request quote"
        canonicalPath="/contact"
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-neutral-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Contact Us</h1>
          <p className="text-xl text-neutral-200">Let's discuss your outdoor living project</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-light text-neutral-900 mb-8">Get in Touch</h2>
              <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
                Whether you're ready to start your project or simply exploring options, we're here to help. 
                Contact us for a free consultation and quote for your outdoor living space.
              </p>

              <div className="space-y-6">
                <Card className="border-neutral-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-neutral-900 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-1">Phone</h3>
                        <a
                          href="tel:07846923387"
                          className="text-neutral-600 hover:text-neutral-900 transition-colors"
                        >
                          07846 923387
                        </a>
                        <p className="text-sm text-neutral-500 mt-1">Monday - Saturday, 8am - 6pm</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-neutral-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-neutral-900 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-1">Email</h3>
                        <a
                          href="mailto:admin@hudsonknox.co.uk"
                          className="text-neutral-600 hover:text-neutral-900 transition-colors"
                        >
                          admin@hudsonknox.co.uk
                        </a>
                        <p className="text-sm text-neutral-500 mt-1">We'll respond within 24 hours</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-neutral-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-neutral-900 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-1">Service Area</h3>
                        <p className="text-neutral-600">London & Kent, UK</p>
                        <p className="text-sm text-neutral-500 mt-1">Free site surveys available</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-neutral-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-neutral-900 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-1">Working Hours</h3>
                        <div className="text-neutral-600 space-y-1">
                          <p>Monday - Friday: 8:00 - 18:00</p>
                          <p>Saturday: 9:00 - 16:00</p>
                          <p>Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 p-6 bg-neutral-100 rounded-lg">
                <h3 className="text-xl font-medium text-neutral-900 mb-3">What to Expect</h3>
                <ul className="space-y-2 text-neutral-600">
                  <li className="flex items-start space-x-2">
                    <span className="text-neutral-900 font-medium">1.</span>
                    <span>Free consultation and site survey at your property</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-neutral-900 font-medium">2.</span>
                    <span>Bespoke design and detailed quotation</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-neutral-900 font-medium">3.</span>
                    <span>Professional installation by our expert team</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-neutral-900 font-medium">4.</span>
                    <span>Ongoing support and warranty coverage</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Quote Form */}
            <div>
              <QuoteForm title="Request Your Free Quote" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-8 text-center">Common Questions</h2>
          <div className="space-y-6">
            {[
              {
                question: "How long does installation take?",
                answer: "Most pergola installations are completed within 2-5 days, depending on size and complexity."
              },
              {
                question: "Do you offer finance options?",
                answer: "Yes, we offer flexible payment plans. Contact us to discuss financing options for your project."
              },
              {
                question: "What's included in the quote?",
                answer: "Our quotes include design, materials, installation, and warranty. We provide transparent pricing with no hidden costs."
              },
              {
                question: "Do I need planning permission?",
                answer: "Most pergolas fall under permitted development, but we can advise on your specific situation during the consultation."
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-neutral-200 pb-6">
                <h3 className="text-xl font-medium text-neutral-900 mb-2">{faq.question}</h3>
                <p className="text-neutral-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
